import { unzipSync, strFromU8 } from 'fflate'

/**
 * Reading CSIU exports — REQUIREMENTS.md §6.4.
 *
 * A deliberately small xlsx reader rather than a spreadsheet library. What
 * arrives here is one machine-generated sheet with a header row and flat
 * values: no formulas, no merged cells, no styles worth honouring. A general
 * library would carry far more surface than that needs, and the widely used
 * one has a history of advisories against a parser we would be feeding
 * files from an external accounting system.
 *
 * Dates are left exactly as stored — Excel serial numbers. Converting them
 * belongs with the rest of the CSIU semantics in `csiuImport`, not here.
 */

/**
 * The XML is read with targeted matching rather than `DOMParser`.
 *
 * `DOMParser` exists only in browsers, and this package has to run unchanged
 * in Cloud Functions and in the Node test runner — that is the whole point of
 * keeping it dependency-light. Hand-matching XML is usually a mistake, but the
 * scope here is two element shapes from a machine-generated file that has no
 * namespaces in practice, no CDATA, and no comments.
 */

export interface SheetTable {
  headers: string[]
  /** One object per data row, keyed by header. Missing cells are absent. */
  rows: Record<string, string>[]
}

/** Anything that isn't a readable spreadsheet, with a reason the user can act on. */
export class UnreadableSpreadsheet extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'UnreadableSpreadsheet'
  }
}

export function isXlsx(bytes: Uint8Array): boolean {
  // Every xlsx is a zip, and every zip starts `PK\x03\x04`.
  return bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04
}

/**
 * The only entries this reader ever looks at. A CSIU export is worksheets and a
 * shared-strings table; it carries no images, fonts, or embedded objects. So
 * nothing else is worth decompressing — and refusing to decompress the rest is
 * most of the defence against a crafted archive, since a zip bomb hides its
 * payload in an entry the reader would otherwise inflate and never use.
 */
const READ_ENTRY = /^xl\/(worksheets\/sheet\d+\.xml|sharedStrings\.xml)$/

/**
 * Cap on a single entry's *uncompressed* size. A real CSIU sheet is a few
 * hundred rows — well under a megabyte — so 25 MB is generous headroom that a
 * legitimate export will never approach, while a small archive that claims to
 * expand into gigabytes is refused before it is inflated.
 *
 * This reads the size the archive declares for the entry. An archive that lies
 * — claiming little, expanding large — is not fully caught here, but the file
 * is hand-picked from the district's own accounting system and this runs in the
 * browser, so the worst a lie could do is crash the importer's tab.
 */
const MAX_ENTRY_BYTES = 25 * 1024 * 1024

/**
 * Whether a zip entry should be decompressed: one this reader uses, and within
 * the size cap. Named and exported so the policy is tested directly, without
 * having to fabricate a multi-megabyte archive to exercise the cap.
 */
export function shouldDecompressEntry(name: string, originalSize: number): boolean {
  return READ_ENTRY.test(name) && originalSize <= MAX_ENTRY_BYTES
}

/**
 * Read the first worksheet of an xlsx into a table.
 *
 * Cell references are honoured rather than assuming cells appear in order and
 * without gaps: an empty cell is simply omitted from the XML, so counting
 * elements would shift every value after the first blank into the wrong
 * column. That misfiling would be invisible — amounts landing under
 * `VendorCode` still look like data.
 */
export function readXlsx(bytes: Uint8Array): SheetTable {
  return tableFromGrid(readXlsxGrid(bytes))
}

/**
 * The first worksheet as raw rows, with no row assumed to be the header.
 *
 * A CSIU export puts its headings on row 1, but a budget workbook built by a
 * person does not — the district's has three title rows and a blank above
 * them. `readXlsx` would take "South Fayette Township School District" as the
 * header and file every amount under it. Callers that have to find their own
 * header row start here.
 */
export function readXlsxGrid(bytes: Uint8Array): string[][] {
  let files: Record<string, Uint8Array>
  try {
    // Decompress only the entries this reader uses, and only if each is within
    // the size cap. A returned false skips the entry entirely — it is never
    // inflated — so an archive padded with a giant unrelated entry costs
    // nothing, and one whose sheet or strings table declares an absurd size is
    // refused rather than expanded.
    files = unzipSync(bytes, {
      filter: (file) => shouldDecompressEntry(file.name, file.originalSize),
    })
  } catch {
    throw new UnreadableSpreadsheet(
      "This file isn't a readable spreadsheet. Save or export it again as .xlsx.",
    )
  }

  const sheetName = firstSheetPath(files)
  const sheetXml = files[sheetName]
  if (sheetXml === undefined) {
    throw new UnreadableSpreadsheet('That workbook has no worksheets.')
  }

  const shared = readSharedStrings(files)
  return readSheetRows(strFromU8(sheetXml), shared)
}

/** Row 1 as headers, every later non-blank row keyed by them. */
function tableFromGrid(grid: readonly string[][]): SheetTable {
  const [headerRow, ...table] = grid
  if (headerRow === undefined) {
    throw new UnreadableSpreadsheet('That sheet is empty.')
  }

  const headers = Array.from(headerRow, (h) => (h ?? '').trim())
  const rows: Record<string, string>[] = []
  for (const cells of table) {
    // A row of nothing but blanks is spacing, not data.
    if (cells.every((c) => c === undefined || c === '')) continue
    const record: Record<string, string> = {}
    headers.forEach((header, index) => {
      if (header === '') return
      const value = cells[index]
      if (value !== undefined && value !== '') record[header] = value
    })
    if (Object.keys(record).length > 0) rows.push(record)
  }

  return { headers: headers.filter((h) => h !== ''), rows }
}

function firstSheetPath(files: Record<string, Uint8Array>): string {
  const sheets = Object.keys(files)
    .filter((name) => /^xl\/worksheets\/sheet\d+\.xml$/.test(name))
    .sort()
  return sheets[0] ?? 'xl/worksheets/sheet1.xml'
}

/** Every `<si>` in sharedStrings, in order. */
function readSharedStrings(files: Record<string, Uint8Array>): string[] {
  const raw = files['xl/sharedStrings.xml']
  if (raw === undefined) return []

  const xml = strFromU8(raw)
  const strings: string[] = []
  for (const [, body] of xml.matchAll(/<si(?:\s[^>]*)?>([\s\S]*?)<\/si>/g)) {
    strings.push(joinTextRuns(body ?? ''))
  }
  return strings
}

/**
 * A string is split across several `<t>` runs when parts of it are styled
 * differently, so the runs are concatenated. Taking only the first would
 * silently truncate any vendor name with a bold word in it.
 */
function joinTextRuns(fragment: string): string {
  let text = ''
  for (const [, run] of fragment.matchAll(/<t(?:\s[^>]*)?>([\s\S]*?)<\/t>/g)) {
    text += decodeXml(run ?? '')
  }
  return text
}

/** Rows of the sheet, indexed by real column so blanks don't shift anything. */
function readSheetRows(xml: string, shared: string[]): string[][] {
  const table: string[][] = []

  for (const [, rowBody] of xml.matchAll(/<row(?:\s[^>]*)?>([\s\S]*?)<\/row>/g)) {
    const cells: string[] = []
    const body = rowBody ?? ''

    // Both forms occur: `<c .../>` for an empty cell, `<c ...>…</c>` otherwise.
    for (const match of body.matchAll(/<c\s([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const attributes = match[1] ?? ''
      const index = columnIndex(/r="([A-Z]+\d+)"/.exec(attributes)?.[1] ?? '')
      if (index < 0) continue
      cells[index] = cellValue(attributes, match[2] ?? '', shared)
    }
    table.push(cells)
  }

  return table
}

function cellValue(attributes: string, body: string, shared: string[]): string {
  const type = /t="([^"]+)"/.exec(attributes)?.[1]

  // Inline strings live in <is><t>, not <v>.
  if (type === 'inlineStr') return joinTextRuns(body)

  const raw = /<v(?:\s[^>]*)?>([\s\S]*?)<\/v>/.exec(body)?.[1]
  if (raw === undefined) return ''

  const value = decodeXml(raw)
  if (type === 's') return shared[Number(value)] ?? ''
  return value
}

function decodeXml(text: string): string {
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, code: string) => String.fromCodePoint(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, code: string) =>
      String.fromCodePoint(Number.parseInt(code, 16)),
    )
    // Ampersand last, or `&amp;lt;` would decode twice into a tag.
    .replace(/&amp;/g, '&')
}

/** `BQ12` → 68. Spreadsheet columns are base-26 with no zero. */
function columnIndex(reference: string): number {
  const letters = /^([A-Z]+)/.exec(reference.toUpperCase())?.[1]
  if (letters === undefined) return -1
  let index = 0
  for (const character of letters) {
    index = index * 26 + (character.charCodeAt(0) - 64)
  }
  return index - 1
}

/**
 * Read a CSV export.
 *
 * Quoted fields are handled because a CSIU description can contain a comma —
 * `"Hershey Lodge, PA - LODGING"` splits into two columns otherwise and shifts
 * every value after it, which misfiles rows rather than failing.
 */
export function readCsv(text: string): SheetTable {
  const table = parseCsvRows(text)
  const headerRow = table.shift()
  if (headerRow === undefined) throw new UnreadableSpreadsheet('That file is empty.')

  const headers = headerRow.map((h) => h.trim())
  const rows: Record<string, string>[] = []
  for (const cells of table) {
    if (cells.every((c) => c.trim() === '')) continue
    const record: Record<string, string> = {}
    headers.forEach((header, index) => {
      if (header === '') return
      const value = cells[index]?.trim() ?? ''
      if (value !== '') record[header] = value
    })
    if (Object.keys(record).length > 0) rows.push(record)
  }
  return { headers: headers.filter((h) => h !== ''), rows }
}

function parseCsvRows(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let quoted = false

  // Normalized first so a CRLF file doesn't leave \r on every last field.
  const input = text.replace(/\r\n?/g, '\n')

  for (let i = 0; i < input.length; i += 1) {
    const character = input[i]

    if (quoted) {
      if (character === '"') {
        // A doubled quote inside a quoted field is a literal quote.
        if (input[i + 1] === '"') {
          field += '"'
          i += 1
        } else {
          quoted = false
        }
      } else {
        field += character
      }
      continue
    }

    if (character === '"') quoted = true
    else if (character === ',') {
      row.push(field)
      field = ''
    } else if (character === '\n') {
      row.push(field)
      rows.push(row)
      row = []
      field = ''
    } else field += character
  }

  if (field !== '' || row.length > 0) {
    row.push(field)
    rows.push(row)
  }
  return rows
}

/** Read whichever format this file is, by content rather than by extension. */
export function readSpreadsheet(bytes: Uint8Array, filename: string): SheetTable {
  if (isXlsx(bytes)) return readXlsx(bytes)

  const text = strFromU8(bytes)
  // A CSIU CSV always carries its header row, so this is a cheap sanity check
  // that we were handed a table and not, say, a PDF renamed to .csv.
  if (!text.includes(',') && !text.includes('\t')) {
    throw new UnreadableSpreadsheet(
      `${filename} doesn't look like a CSIU export. Export it as .xlsx or .csv.`,
    )
  }
  return readCsv(text)
}

/**
 * Any supported file as raw rows — xlsx or CSV — with no header assumed.
 *
 * The grid counterpart of `readSpreadsheet`, for files whose headings are not
 * on the first row. The CSV check is looser than that function's, because the
 * message there names CSIU and this reads files people build themselves.
 */
export function readSpreadsheetGrid(bytes: Uint8Array, filename: string): string[][] {
  if (isXlsx(bytes)) return readXlsxGrid(bytes)

  const text = strFromU8(bytes)
  if (!text.includes(',') && !text.includes('\t')) {
    throw new UnreadableSpreadsheet(
      `${filename} isn't a spreadsheet this can read. Save it as .xlsx or .csv.`,
    )
  }
  return parseCsvRows(text)
}
